import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { AppSettingsService } from './app-settings.service';
import {
  DeviceSignalsDto,
  GeoLocation,
  ResolveDeviceRequestDto,
  ResolveDeviceResponseDto,
} from 'src/app/system/models/known-device';

export interface KnownDeviceIdentity {
  deviceId: string | null;
  browserId: string | null;
}

const FONT_PROBE_LIST: readonly string[] = [
  'Arial', 'Arial Black', 'Calibri', 'Cambria', 'Candara', 'Comic Sans MS', 'Consolas',
  'Courier New', 'Franklin Gothic Medium', 'Georgia', 'Impact', 'Lucida Console', 'Palatino Linotype',
  'Segoe UI', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana', 'Helvetica', 'Sylfaen',
  'Aptos', 'Arial Narrow', 'Book Antiqua', 'Bookman Old Style', 'Century Gothic', 'Garamond',
  'Gill Sans MT', 'Lucida Sans', 'Monotype Corsiva', 'Rockwell', 'Tw Cen MT', 'Agency FB',
  'Arabic Typesetting', 'Traditional Arabic', 'Simplified Arabic', 'Sakkal Majalla', 'Aldhabi', 'Andalus',
  'Cascadia Code', 'Fira Code', 'JetBrains Mono', 'Source Code Pro',
  'Cairo', 'Tajawal', 'Almarai', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Noto Sans',
  'Myriad Pro', 'Minion Pro', 'Adobe Arabic',
  'Helvetica Neue', 'Menlo', 'Monaco', 'Ubuntu', 'DejaVu Sans', 'Liberation Sans',
];

@Injectable({ providedIn: 'root' })
export class KnownDeviceIdentityService {
  private static readonly TOKEN_KEY = 'knownDeviceToken';

  // Free, key-less, HTTPS + CORS IP-geolocation lookup (returns { country_name, city, region } for the
  // caller's public IP). Swap the URL/mapping in resolveGeo() to use a different provider (or your own
  // endpoint) if you prefer not to call a third party from the browser.
  private static readonly GEO_ENDPOINT = 'https://ipapi.co/json/';
  private static readonly GEO_TIMEOUT_MS = 2000;

  private readonly baseUrl: string;

  private deviceId: string | null = null;
  private browserId: string | null = null;

  private resolution?: Promise<KnownDeviceIdentity>;

  constructor(private httpClient: HttpClient) {
    const url = AppSettingsService.appSettings.edaraCoreApi.baseUrl;
    this.baseUrl = (url.endsWith('/') ? url.slice(0, -1) : url) + '/api/administration/devices';
  }

  get identity(): KnownDeviceIdentity {
    return { deviceId: this.deviceId, browserId: this.browserId };
  }

  resolveDevice(): Promise<KnownDeviceIdentity> {
    this.resolution ??= this.runResolve().then(identity => {
      if (!identity.deviceId) this.resolution = undefined;   // a failed resolve is retried on the next call
      return identity;
    });
    return this.resolution;
  }

  clearIdentity(): void {
    this.deviceId = null;
    this.browserId = null;
    this.resolution = undefined;
    localStorage.removeItem(KnownDeviceIdentityService.TOKEN_KEY);
  }

  // --- resolve ---------------------------------------------------------------------

  private async runResolve(): Promise<KnownDeviceIdentity> {
    try {
      const body: ResolveDeviceRequestDto = { signals: await this.collectSignals() };

      const token = this.readToken();
      if (token) {
        body.token = token;
      }

      const geo = await this.resolveGeo();
      if (geo) {
        body.geo = geo;
      }

      const response = await firstValueFrom(
        this.httpClient.post<ResolveDeviceResponseDto>(`${this.baseUrl}/resolve`, body)
      );

      if (response?.token) {
        this.writeToken(response.token);
      }
      this.deviceId = response?.deviceId ?? this.deviceId;
      this.browserId = response?.browserId ?? this.browserId;
    } catch (error) {
      console.warn('[known-device-identity] resolve failed; continuing without device identity', error);
    }

    return this.identity;
  }

  // --- geo -------------------------------------------------------------------------

  // Resolve the caller's approximate location from its public IP and return { country, city, region },
  // or null to omit it. Uses native fetch (NOT Angular HttpClient) so no Edara auth header is sent to the
  // third party, with a short timeout so a slow/unreachable provider never stalls login. The backend
  // stores whatever is sent with the login's security events and returns it as-is in the WebID log.
  private async resolveGeo(): Promise<GeoLocation | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), KnownDeviceIdentityService.GEO_TIMEOUT_MS);
    try {
      const response = await fetch(KnownDeviceIdentityService.GEO_ENDPOINT, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as {
        error?: boolean;
        country_name?: string;
        country?: string;
        city?: string;
        region?: string;
        region_name?: string;
        state?: string;
      };
      if (data.error) {
        return null;
      }

      const geo: GeoLocation = {
        country: data.country_name ?? data.country ?? null,
        city: data.city ?? null,
        region: data.region ?? data.region_name ?? data.state ?? null,
      };
      return geo.country || geo.city || geo.region ? geo : null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  // --- token store -----------------------------------------------------------------

  private readToken(): string | null {
    return localStorage.getItem(KnownDeviceIdentityService.TOKEN_KEY);
  }

  private writeToken(token: string): void {
    localStorage.setItem(KnownDeviceIdentityService.TOKEN_KEY, token);
  }

  // --- signals ---------------------------------------------------------------------

  private async collectSignals(): Promise<DeviceSignalsDto> {
    const signals: DeviceSignalsDto = {};

    this.add(signals, 'osFamily', () => this.readOsFamily());
    this.add(signals, 'screen', () => this.readScreen());
    this.add(signals, 'fonts', () => this.readFonts());
    this.add(signals, 'cpuCores', () => navigator.hardwareConcurrency);
    this.add(signals, 'timezone', () => Intl.DateTimeFormat().resolvedOptions().timeZone);
    this.add(signals, 'deviceMemoryGb', () => (navigator as Navigator & { deviceMemory?: number }).deviceMemory);

    this.add(signals, 'gpu', () => this.readGpu());

    this.add(signals, 'userAgent', () => navigator.userAgent);
    this.add(signals, 'canvasFingerprint', () => this.readCanvasFingerprint());
    this.add(signals, 'languages', () => [...(navigator.languages ?? [])]);
    this.add(signals, 'devicePixelRatio', () => window.devicePixelRatio);

    const audioFingerprint = await this.readAudioFingerprint();
    if (audioFingerprint) {
      signals.audioFingerprint = audioFingerprint;
    }

    return signals;
  }

  private add<K extends keyof DeviceSignalsDto>(
    signals: DeviceSignalsDto,
    key: K,
    read: () => DeviceSignalsDto[K] | undefined
  ): void {
    const value = read();
    if (value === undefined || value === null || value === 0 || value === '') {
      return;
    }
    if (Array.isArray(value) && !value.length) {
      return;
    }
    signals[key] = value;
  }

  private readOsFamily(): string | undefined {
    const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
    const source = platform || navigator.userAgent;

    if (/iphone|ipad|ipod|ios/i.test(source)) return 'iOS';
    if (/android/i.test(source)) return 'Android';
    if (/win/i.test(source)) return 'Windows';
    if (/mac/i.test(source)) return 'macOS';
    if (/linux|x11|cros/i.test(source)) return 'Linux';
    return undefined;
  }

  private readScreen(): DeviceSignalsDto['screen'] {
    const { width, height, colorDepth } = window.screen;
    if (!width || !height) {
      return undefined;
    }
    return colorDepth ? { width, height, colorDepth } : { width, height };
  }

  private readFonts(): string[] | undefined {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return undefined;
    }

    const probe = 'mmmmmmmmmmlli';
    const bases = ['monospace', 'sans-serif', 'serif'];
    const baseWidths = bases.map((base) => {
      context.font = `72px ${base}`;
      return context.measureText(probe).width;
    });

    return FONT_PROBE_LIST.filter((font) =>
      bases.some((base, index) => {
        context.font = `72px "${font}", ${base}`;
        return context.measureText(probe).width !== baseWidths[index];
      })
    );
  }

  private readGpu(): string | undefined {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      return undefined;
    }
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    return info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : undefined;
  }

  private readCanvasFingerprint(): string | undefined {
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 60;
    const context = canvas.getContext('2d');
    if (!context) {
      return undefined;
    }

    context.textBaseline = 'top';
    context.font = '14px "Arial"';
    context.fillStyle = '#f60';
    context.fillRect(0, 0, 100, 20);
    context.fillStyle = '#069';
    context.fillText('Edara device 01', 2, 15);
    context.fillStyle = 'rgba(102, 204, 0, 0.7)';
    context.fillText('Edara device 01', 4, 25);

    return this.hash(canvas.toDataURL());
  }

  private async readAudioFingerprint(): Promise<string | undefined> {
    const AudioContextCtor =
      window.OfflineAudioContext ??
      (window as Window & { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
    if (!AudioContextCtor) {
      return undefined;
    }

    const context = new AudioContextCtor(1, 5000, 44100);
    const oscillator = context.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.value = 10000;

    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;

    oscillator.connect(compressor);
    compressor.connect(context.destination);
    oscillator.start(0);

    const buffer = await context.startRendering();
    const samples = buffer.getChannelData(0);

    let total = 0;
    for (let i = 4500; i < samples.length; i++) {
      total += Math.abs(samples[i]);
    }
    return total ? this.hash(total.toString()) : undefined;
  }

  private hash(value: string): string {
    const fnv = (seed: number): string => {
      let hash = seed;
      for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
      }
      return (hash >>> 0).toString(16).padStart(8, '0');
    };
    return fnv(0x811c9dc5) + fnv(0x0000193b);
  }
}
